/**
 * OrderByte product modal HTML (POST /prodDetail) — variations, toppings, modifiers.
 */

export const ORDERBYTE_MENU_JSON_ID = "restogpt-orderbyte-menu";

function parseMoney(raw) {
  if (raw == null || raw === "") return null;
  const m = String(raw).match(/\$?\s*(\d+(?:\.\d{1,2})?)/);
  if (!m) return null;
  const n = Number.parseFloat(m[1]);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : null;
}

export function parseOrderByteOptionValue(raw) {
  const s = String(raw || "").trim();
  const m = s.match(/^(.+?)_(\d+(?:\.\d{1,2})?)_(\d+)_(.+)$/);
  if (!m) return null;

  let tail = m[4];
  let placement = "default";
  if (/_whole2$/i.test(tail)) {
    placement = "whole";
    tail = tail.replace(/_whole2$/i, "");
  } else if (/_side1$/i.test(tail)) {
    placement = "sideA";
    tail = tail.replace(/_side1$/i, "");
  } else if (/_side2$/i.test(tail)) {
    placement = "sideB";
    tail = tail.replace(/_side2$/i, "");
  }

  return {
    name: m[1].trim(),
    price: Number.parseFloat(m[2]),
    optionId: m[3],
    group: tail.trim(),
    placement,
  };
}

export function resolveProdDetailUrl(html, pageUrl) {
  const m = String(html || "").match(
    /url\s*:\s*['"]([^'"]*prodDetail)['"]/i
  );
  if (!m) return null;
  try {
    return new URL(m[1], pageUrl).href;
  } catch {
    return null;
  }
}

export function resolveProdDetailSubUrl(html, pageUrl) {
  const m = String(html || "").match(
    /url\s*:\s*['"]([^'"]*prodDetailSub)['"]/i
  );
  if (!m) return null;
  try {
    return new URL(m[1], pageUrl).href;
  } catch {
    return null;
  }
}

/** Unique products from menu cards (prodo_ / produ_ ids). */
export function extractOrderByteProductRefs(html) {
  const refs = new Map();
  const re = /\bid="(?:prodo|produ)_(\d+)_(\d+)_/gi;
  let match;
  const src = String(html || "");
  while ((match = re.exec(src)) !== null) {
    const productId = match[1];
    if (!refs.has(productId)) {
      refs.set(productId, {
        productId,
        pic: match[2],
        siz: "",
        endpoint: "prodDetail",
      });
    }
  }

  const subRe = /\bid="[^"]*subs-1[^"]*"\s+id="[^"]*_(\d+)_(\d+)_/gi;
  while ((match = subRe.exec(src)) !== null) {
    const productId = match[1];
    refs.set(productId, {
      productId,
      pic: match[2],
      siz: "",
      endpoint: "prodDetailSub",
    });
  }

  return [...refs.values()];
}

function parseToppingItemBlock(block) {
  const name =
    block.match(/id="nametag_[^"]*"[^>]*>\s*([^<]+)/i)?.[1]?.trim() ||
    block.match(/<p[^>]*>\s*([^<]+)\s*<\/p>/i)?.[1]?.trim();
  if (!name) return null;

  const prices = {};
  const inputs = [...block.matchAll(/class="opt"[^>]*value="([^"]+)"/gi)];
  for (const [, value] of inputs) {
    const parsed = parseOrderByteOptionValue(value);
    if (!parsed) continue;
    if (parsed.placement === "whole") prices.whole = parsed.price;
    else if (parsed.placement === "sideA") prices.sideA = parsed.price;
    else if (parsed.placement === "sideB") prices.sideB = parsed.price;
    else prices.default = parsed.price;
  }

  const tagPrice = parseMoney(
    block.match(/id="pricetag_[^"]*"[^>]*>[\s\S]*?(\$?\s*\d+\.\d{2})/i)?.[1]
  );
  if (tagPrice != null && !Object.keys(prices).length) {
    prices.default = tagPrice;
  }

  if (!Object.keys(prices).length) return null;

  return { name, prices };
}

function parseModifierGroupsFromDetail(html) {
  const src = String(html || "");
  const groups = [];
  const headings = [...src.matchAll(/<h5[^>]*id="ind_grp_nm[^"]*"[^>]*>([^<]*)<\/h5>/gi)];

  for (let i = 0; i < headings.length; i += 1) {
    const name = headings[i][1].replace(/\s+/g, " ").trim();
    if (!name) continue;

    const start = headings[i].index;
    const end = headings[i + 1]?.index ?? src.length;
    const chunk = src.slice(start, end);

    const selectionHint = /choose only one/i.test(chunk) ? "single" : "multiple";
    const hasColumns = /SIDE A/i.test(chunk) && /WHOLE/i.test(chunk);

    if (/topping-item/i.test(chunk)) {
      const options = [];
      const items = chunk.split(/class="topping-item"/i).slice(1);
      for (const itemBlock of items) {
        const opt = parseToppingItemBlock(itemBlock);
        if (opt) options.push(opt);
      }
      groups.push({
        name,
        selectionType: selectionHint,
        ...(hasColumns ? { columns: ["whole", "sideB", "sideA"] } : {}),
        options,
      });
      continue;
    }

    const options = [];
    const seen = new Set();
    const values = [...chunk.matchAll(/class="opt"[^>]*value="([^"]+)"/gi)];
    for (const [, value] of values) {
      const parsed = parseOrderByteOptionValue(value);
      if (!parsed) continue;
      const key = `${parsed.name}|${parsed.price}|${parsed.placement}`;
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({
        name: parsed.name,
        price: parsed.price,
        ...(parsed.placement !== "default" ? { placement: parsed.placement } : {}),
      });
    }

    if (options.length) {
      groups.push({
        name,
        selectionType: selectionHint,
        options,
      });
    }
  }

  return groups;
}

export function parseProductDetailHtml(html, productId) {
  const src = String(html || "");
  if (!src.includes("indgDetails") && src.length < 200) {
    return null;
  }

  const name =
    src.match(/class="top-content-box"[\s\S]*?<h3[^>]*>([^<]+)<\/h3>/i)?.[1]?.trim() ||
    src.match(/<h3[^>]*>([^<]+)<\/h3>/i)?.[1]?.trim();
  if (!name) return null;

  const basePrice = parseMoney(
    src.match(/class="title"[\s\S]*?<h4[^>]*>[\s\S]*?(\d+\.\d{2})/i)?.[1]
  );

  const descCandidates = [
    src.match(/class="top-content-box"[\s\S]*?<p>([^<]+)<\/p>/i)?.[1]?.trim(),
    src.match(/class="desc"[\s\S]*?<p>([^<]+)<\/p>/i)?.[1]?.trim(),
  ].filter(Boolean);
  const description =
    descCandidates.find(
      (p) => p.length >= 8 && !/^(quantity|required|optional)$/i.test(p)
    ) || null;

  const modifierGroups = parseModifierGroupsFromDetail(src);

  return {
    productId: String(productId),
    name,
    ingredients: description,
    price: basePrice,
    modifierGroups,
  };
}

async function mapPool(items, concurrency, worker) {
  const results = [];
  let index = 0;

  async function runNext() {
    while (index < items.length) {
      const i = index;
      index += 1;
      results[i] = await worker(items[i], i);
    }
  }

  const runners = Array.from({ length: Math.min(concurrency, items.length) }, () =>
    runNext()
  );
  await Promise.all(runners);
  return results;
}

/**
 * Fetch prodDetail HTML for each product ref and return parsed menu items.
 */
export async function fetchOrderByteProductDetails({
  pageUrl,
  pageHtml,
  cookieHeader,
  fetchFn,
  maxProducts = 120,
  concurrency = 4,
}) {
  const html = String(pageHtml || "");
  const prodDetailUrl =
    resolveProdDetailUrl(html, pageUrl) ||
    new URL("/prodDetail", pageUrl).href;
  const prodDetailSubUrl =
    resolveProdDetailSubUrl(html, pageUrl) ||
    new URL("/prodDetailSub", pageUrl).href;

  const refs = extractOrderByteProductRefs(html).slice(0, maxProducts);
  if (!refs.length) return [];

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
    Accept: "text/html,*/*",
    ...(cookieHeader ? { Cookie: cookieHeader } : {}),
  };

  const bodyFor = (ref) =>
    new URLSearchParams({
      val: ref.productId,
      pic: ref.pic,
      siz: ref.siz || "",
    }).toString();

  const fetched = await mapPool(refs, concurrency, async (ref) => {
    const url =
      ref.endpoint === "prodDetailSub" ? prodDetailSubUrl : prodDetailUrl;
    try {
      const res = await fetchFn(url, {
        method: "POST",
        headers,
        body: bodyFor(ref),
      });
      if (!res.ok) return null;
      const text = await res.text();
      return parseProductDetailHtml(text, ref.productId);
    } catch {
      return null;
    }
  });

  return fetched.filter(Boolean);
}

export function injectOrderByteMenuJson(pageHtml, items) {
  const safe = JSON.stringify({ items }).replace(/</g, "\\u003c");
  const script = `<script type="application/json" id="${ORDERBYTE_MENU_JSON_ID}">${safe}</script>`;
  const page = String(pageHtml || "");
  if (page.includes(`id="${ORDERBYTE_MENU_JSON_ID}"`)) {
    return page.replace(
      new RegExp(
        `<script type="application/json" id="${ORDERBYTE_MENU_JSON_ID}">[\\s\\S]*?<\\/script>`
      ),
      script
    );
  }
  if (/<\/body>/i.test(page)) {
    return page.replace(/<\/body>/i, `${script}</body>`);
  }
  return `${page}${script}`;
}

export function readEmbeddedOrderByteMenu(html) {
  const src = String(html || "");
  const m = src.match(
    new RegExp(
      `<script[^>]+id="${ORDERBYTE_MENU_JSON_ID}"[^>]*>([\\s\\S]*?)<\\/script>`,
      "i"
    )
  );
  if (!m) return null;
  try {
    const data = JSON.parse(m[1]);
    if (!Array.isArray(data?.items)) return null;
    return data.items;
  } catch {
    return null;
  }
}
